export type HeightReference = 'absolute' | 'clamp-to-ground' | 'relative-to-ground' | 'clamp-to-3dtiles' | 'relative-to-3dtiles';

export interface MINearFarScalar {
  near: number;
  nearValue: number;
  far: number;
  farValue: number;
}

export interface MIClippingPlane {
  normal: [number, number, number];
  distance: number;
  enabled?: boolean;
  edgeColor?: string;
  edgeWidth?: number;
  unionClippingRegions?: boolean;
}

export interface MIMaterial {
  type: 'color' | 'image' | 'checkerboard' | 'stripe' | 'grid' | 'polyline-glow' | 'polyline-arrow' | 'polyline-dash' | 'custom';
  color?: string;
  image?: string;
  repeat?: [number, number];
  transparent?: boolean;
  alpha?: number;
  lightColor?: string;
  darkColor?: string;
  evenColor?: string;
  oddColor?: string;
  orientation?: 'horizontal' | 'vertical';
  offset?: number;
  cellAlpha?: number;
  lineCount?: [number, number];
  lineThickness?: [number, number];
  lineOffset?: [number, number];
  glowPower?: number;
  taperPower?: number;
  fragmentShader?: string;
  uniforms?: Record<string, any>;
}

export interface MI3DSymbol {
  heightReference?: HeightReference;
  distanceDisplayCondition?: [number, number];
  disableDepthTest?: boolean;
  castShadows?: boolean;
  receiveShadows?: boolean;
}

export interface MIModelSymbolOptions extends MI3DSymbol {
  url: string;
  scale?: number | [number, number, number];
  heading?: number;
  pitch?: number;
  roll?: number;
  color?: string;
  colorBlendMode?: 'highlight' | 'replace' | 'mix';
  colorBlendAmount?: number;
  silhouetteColor?: string;
  silhouetteSize?: number;
  minimumPixelSize?: number;
  maximumScale?: number;
  shadows?: 'enabled' | 'disabled' | 'cast-only' | 'receive-only';
  lightColor?: string;
  clippingPlanes?: MIClippingPlane[];
  distanceDisplayCondition?: [number, number];
  animationIndex?: number;
  animationSpeed?: number;
  animationLoop?: boolean;
}

export class MIModelSymbol implements MI3DSymbol {
  public url: string;
  public scale?: number | [number, number, number];
  public heading?: number;
  public pitch?: number;
  public roll?: number;
  public color?: string;
  public colorBlendMode?: 'highlight' | 'replace' | 'mix';
  public colorBlendAmount?: number;
  public silhouetteColor?: string;
  public silhouetteSize?: number;
  public minimumPixelSize?: number;
  public maximumScale?: number;
  public shadows?: 'enabled' | 'disabled' | 'cast-only' | 'receive-only';
  public lightColor?: string;
  public clippingPlanes?: MIClippingPlane[];
  public heightReference?: HeightReference;
  public distanceDisplayCondition?: [number, number];
  public disableDepthTest?: boolean;
  public castShadows?: boolean;
  public receiveShadows?: boolean;
  public animationIndex?: number;
  public animationSpeed?: number;
  public animationLoop?: boolean;

  private _animationNames: string[] = [];

  constructor(options: MIModelSymbolOptions) {
    this.url = options.url;
    this.scale = options.scale;
    this.heading = options.heading;
    this.pitch = options.pitch;
    this.roll = options.roll;
    this.color = options.color;
    this.colorBlendMode = options.colorBlendMode;
    this.colorBlendAmount = options.colorBlendAmount;
    this.silhouetteColor = options.silhouetteColor;
    this.silhouetteSize = options.silhouetteSize;
    this.minimumPixelSize = options.minimumPixelSize;
    this.maximumScale = options.maximumScale;
    this.shadows = options.shadows;
    this.lightColor = options.lightColor;
    this.clippingPlanes = options.clippingPlanes;
    this.heightReference = options.heightReference;
    this.distanceDisplayCondition = options.distanceDisplayCondition;
    this.disableDepthTest = options.disableDepthTest;
    this.castShadows = options.castShadows;
    this.receiveShadows = options.receiveShadows;
    this.animationIndex = options.animationIndex;
    this.animationSpeed = options.animationSpeed;
    this.animationLoop = options.animationLoop;
  }

  playAnimation(_index?: number, _options?: { speed?: number; loop?: boolean; reverse?: boolean }): void {
    if (_index !== undefined) {
      this.animationIndex = _index;
    }
    if (_options) {
      if (_options.speed !== undefined) this.animationSpeed = _options.speed;
      if (_options.loop !== undefined) this.animationLoop = _options.loop;
    }
  }

  stopAnimation(): void {
    this.animationSpeed = 0;
  }

  getAnimationNames(): string[] {
    return [...this._animationNames];
  }

  getBoundingBox(): any {
    return null;
  }

  setArticulation(_name: string, _value: number): void {
  }
}

export interface MIExtrudedSymbolOptions {
  height: number | string;
  baseHeight?: number | string;
  heightReference?: HeightReference;
  material: MIMaterial;
  topMaterial?: MIMaterial;
  outlineColor?: string;
  outlineWidth?: number;
  shadows?: 'enabled' | 'disabled' | 'cast-only' | 'receive-only';
  closeTop?: boolean;
  closeBottom?: boolean;
  distanceDisplayCondition?: [number, number];
}

export class MIExtrudedSymbol {
  public height: number | string;
  public baseHeight?: number | string;
  public heightReference?: HeightReference;
  public material: MIMaterial;
  public topMaterial?: MIMaterial;
  public outlineColor?: string;
  public outlineWidth?: number;
  public shadows?: 'enabled' | 'disabled' | 'cast-only' | 'receive-only';
  public closeTop?: boolean;
  public closeBottom?: boolean;
  public distanceDisplayCondition?: [number, number];

  constructor(options: MIExtrudedSymbolOptions) {
    this.height = options.height;
    this.baseHeight = options.baseHeight;
    this.heightReference = options.heightReference;
    this.material = options.material;
    this.topMaterial = options.topMaterial;
    this.outlineColor = options.outlineColor;
    this.outlineWidth = options.outlineWidth;
    this.shadows = options.shadows;
    this.closeTop = options.closeTop;
    this.closeBottom = options.closeBottom;
    this.distanceDisplayCondition = options.distanceDisplayCondition;
  }
}

export interface MIBillboardSymbolOptions {
  image: string | HTMLCanvasElement;
  width?: number;
  height?: number;
  anchor?: [number, number];
  rotation?: number;
  alignedAxis?: [number, number, number];
  sizeInMeters?: boolean;
  scale?: number;
  color?: string;
  heightReference?: HeightReference;
  scaleByDistance?: MINearFarScalar;
  translucencyByDistance?: MINearFarScalar;
  pixelOffset?: [number, number];
  eyeOffset?: [number, number, number];
  disableDepthTest?: boolean;
  distanceDisplayCondition?: [number, number];
}

export class MIBillboardSymbol {
  public image: string | HTMLCanvasElement;
  public width?: number;
  public height?: number;
  public anchor?: [number, number];
  public rotation?: number;
  public alignedAxis?: [number, number, number];
  public sizeInMeters?: boolean;
  public scale?: number;
  public color?: string;
  public heightReference?: HeightReference;
  public scaleByDistance?: MINearFarScalar;
  public translucencyByDistance?: MINearFarScalar;
  public pixelOffset?: [number, number];
  public eyeOffset?: [number, number, number];
  public disableDepthTest?: boolean;
  public distanceDisplayCondition?: [number, number];

  constructor(options: MIBillboardSymbolOptions) {
    this.image = options.image;
    this.width = options.width;
    this.height = options.height;
    this.anchor = options.anchor;
    this.rotation = options.rotation;
    this.alignedAxis = options.alignedAxis;
    this.sizeInMeters = options.sizeInMeters;
    this.scale = options.scale;
    this.color = options.color;
    this.heightReference = options.heightReference;
    this.scaleByDistance = options.scaleByDistance;
    this.translucencyByDistance = options.translucencyByDistance;
    this.pixelOffset = options.pixelOffset;
    this.eyeOffset = options.eyeOffset;
    this.disableDepthTest = options.disableDepthTest;
    this.distanceDisplayCondition = options.distanceDisplayCondition;
  }
}

export interface MILabelSymbol3DOptions {
  text: string | ((feature: any) => string);
  font?: string;
  fillColor?: string;
  outlineColor?: string;
  outlineWidth?: number;
  backgroundColor?: string;
  backgroundPadding?: [number, number];
  heightReference?: HeightReference;
  pixelOffset?: [number, number];
  eyeOffset?: [number, number, number];
  horizontalOrigin?: 'left' | 'center' | 'right';
  verticalOrigin?: 'top' | 'center' | 'bottom' | 'baseline';
  scaleByDistance?: MINearFarScalar;
  translucencyByDistance?: MINearFarScalar;
  distanceDisplayCondition?: [number, number];
  disableDepthTest?: boolean;
  showBackground?: boolean;
  style?: 'fill' | 'outline' | 'fill-and-outline';
}

export class MILabelSymbol3D {
  public text: string | ((feature: any) => string);
  public font?: string;
  public fillColor?: string;
  public outlineColor?: string;
  public outlineWidth?: number;
  public backgroundColor?: string;
  public backgroundPadding?: [number, number];
  public heightReference?: HeightReference;
  public pixelOffset?: [number, number];
  public eyeOffset?: [number, number, number];
  public horizontalOrigin?: 'left' | 'center' | 'right';
  public verticalOrigin?: 'top' | 'center' | 'bottom' | 'baseline';
  public scaleByDistance?: MINearFarScalar;
  public translucencyByDistance?: MINearFarScalar;
  public distanceDisplayCondition?: [number, number];
  public disableDepthTest?: boolean;
  public showBackground?: boolean;
  public style?: 'fill' | 'outline' | 'fill-and-outline';

  constructor(options: MILabelSymbol3DOptions) {
    this.text = options.text;
    this.font = options.font;
    this.fillColor = options.fillColor;
    this.outlineColor = options.outlineColor;
    this.outlineWidth = options.outlineWidth;
    this.backgroundColor = options.backgroundColor;
    this.backgroundPadding = options.backgroundPadding;
    this.heightReference = options.heightReference;
    this.pixelOffset = options.pixelOffset;
    this.eyeOffset = options.eyeOffset;
    this.horizontalOrigin = options.horizontalOrigin;
    this.verticalOrigin = options.verticalOrigin;
    this.scaleByDistance = options.scaleByDistance;
    this.translucencyByDistance = options.translucencyByDistance;
    this.distanceDisplayCondition = options.distanceDisplayCondition;
    this.disableDepthTest = options.disableDepthTest;
    this.showBackground = options.showBackground;
    this.style = options.style;
  }
}

export interface MILineSymbol3DOptions {
  type: 'line' | 'tube' | 'wall' | 'strip';
  width?: number;
  widthUnit?: 'meters' | 'pixels';
  height?: number;
  material: MIMaterial;
  heightReference?: HeightReference;
  depthFailMaterial?: MIMaterial;
  shadows?: 'enabled' | 'disabled';
  distanceDisplayCondition?: [number, number];
  join?: 'miter' | 'bevel' | 'round';
  cornerType?: 'rounded' | 'mitered' | 'beveled';
  granularity?: number;
  arcType?: 'geodesic' | 'rhumb' | 'none';
}

export class MILineSymbol3D {
  public type: 'line' | 'tube' | 'wall' | 'strip';
  public width?: number;
  public widthUnit?: 'meters' | 'pixels';
  public height?: number;
  public material: MIMaterial;
  public heightReference?: HeightReference;
  public depthFailMaterial?: MIMaterial;
  public shadows?: 'enabled' | 'disabled';
  public distanceDisplayCondition?: [number, number];
  public join?: 'miter' | 'bevel' | 'round';
  public cornerType?: 'rounded' | 'mitered' | 'beveled';
  public granularity?: number;
  public arcType?: 'geodesic' | 'rhumb' | 'none';

  constructor(options: MILineSymbol3DOptions) {
    this.type = options.type;
    this.width = options.width;
    this.widthUnit = options.widthUnit;
    this.height = options.height;
    this.material = options.material;
    this.heightReference = options.heightReference;
    this.depthFailMaterial = options.depthFailMaterial;
    this.shadows = options.shadows;
    this.distanceDisplayCondition = options.distanceDisplayCondition;
    this.join = options.join;
    this.cornerType = options.cornerType;
    this.granularity = options.granularity;
    this.arcType = options.arcType;
  }
}
